"use client";

import React, { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Card } from "react-bootstrap";
import { Pagination, Autoplay } from "swiper/modules";

// Helper: Get initials for fallback avatar
const getInitials = (name = "") => {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const PopularRooms = ({ screenshotsData = [] }) => {
  const [displayItems, setDisplayItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (screenshotsData && screenshotsData.length > 0) {
      setDisplayItems(
        screenshotsData.map((ss) => ({
          id: ss.id,
          name: ss.memberName,
          code: ss.application || "Unknown App",
          image: ss.thumbnailUrl,
          status: "Active",
          link: "#",
        }))
      );
    }
  }, [screenshotsData]);

  const openModal = (imageUrl, itemName) => {
    setSelectedImage({ url: imageUrl, name: itemName });
    setModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedImage(null);
    document.body.style.overflow = "auto";
  };

  const handleBackdropClick = (e) => {
    if (e.target.classList.contains("modal-backdrop")) {
      closeModal();
    }
  };

  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === "Escape" && modalOpen) {
        closeModal();
      }
    };

    if (modalOpen) {
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [modalOpen]);

  return (
    <>
      {/* Card Wrapper */}
      <Card className="custom-card mb-4">
        <Card.Body>
          <div className="header">
            <h3>Screenshots</h3>
          </div>

          <Swiper
            spaceBetween={20}
            pagination={{ clickable: true }}
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            breakpoints={{
              0: { slidesPerView: 1 },
              576: { slidesPerView: 2 },
              768: { slidesPerView: 2 },
              992: { slidesPerView: 3 },
              1200: { slidesPerView: 4 },
              1440: { slidesPerView: 4 },
              1850: { slidesPerView: 4 },
            }}
            modules={[Pagination, Autoplay]}
          >
            {displayItems.map((item) => (
              <SwiperSlide key={item.id}>
                <div
                  className="room-card"
                  onClick={() => openModal(item.image, item.name)}
                >
                  {/* Image or fallback */}
                  <div className="room-image">
                    {item.image ? (
                      <img src={item.image} alt={item.name} />
                    ) : (
                      <div className="avatar-fallback">
                        {getInitials(item.name)}
                      </div>
                    )}

                    {/* Status */}
                    <div className="status-badge">
                      <span className="dot"></span>
                      {item.status}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="room-content">
                    <h4 className="room-name">{item.name}</h4>
                    <p className="room-app">{item.code}</p>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </Card.Body>
      </Card>

      {/* Modal */}
      {modalOpen && selectedImage && (
        <div className="modal-backdrop" onClick={handleBackdropClick}>
          <div className="modal-content">
            <button className="close-btn" onClick={closeModal}>
              ×
            </button>

            <img src={selectedImage.url} alt={selectedImage.name} />

            <div className="caption">{selectedImage.name}</div>
          </div>
        </div>
      )}

      {/* Styles */}
      <style jsx>{`
        /* FIX: control bootstrap card size */
        .custom-card {
          border-radius: 12px;
        }

        .custom-card .card-body {
          padding: 16px; /* controlled padding (not oversized) */
        }

        .header {
          margin-bottom: 12px;
        }

        .room-card {
          border-radius: 14px;
          overflow: hidden;
          background: #fff;
          transition: all 0.25s ease;
          cursor: pointer;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
        }

        .room-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
        }

        .room-image {
          position: relative;
          height: 110px;
          background: #f5f6f8;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .room-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .avatar-fallback {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          color: #fff;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .room-content {
          padding: 10px 12px;
        }

        .room-name {
          font-size: 14px;
          font-weight: 600;
          margin: 0;
        }

        .room-app {
          font-size: 12px;
          color: #888;
          margin-top: 2px;
        }

        .status-badge {
          position: absolute;
          top: 8px;
          right: 8px;
          background: rgba(0, 0, 0, 0.6);
          color: #fff;
          font-size: 11px;
          padding: 3px 8px;
          border-radius: 999px;
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .dot {
          width: 6px;
          height: 6px;
          background: #22c55e;
          border-radius: 50%;
        }

        /* Modal */
        .modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.9);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
          padding: 20px;
        }

        .modal-content {
          position: relative;
          max-width: 90vw;
          max-height: 90vh;
        }

        .modal-content img {
          max-width: 100%;
          max-height: 90vh;
          object-fit: contain;
          border-radius: 8px;
        }

        .close-btn {
          position: absolute;
          top: -40px;
          right: 0;
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: white;
          font-size: 24px;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          cursor: pointer;
        }

        .caption {
          color: white;
          text-align: center;
          margin-top: 10px;
          font-size: 16px;
          font-weight: 500;
        }
      `}</style>
    </>
  );
};

export default PopularRooms;